import React, { useEffect, useMemo, useRef, useState } from "react";
import { PUPPET, OPEN_SEQ } from "@/lib/tokens";

/**
 * Web port of the approved puppet_v2 rig. Composites registration-aligned full
 * frames on a fixed canvas: a BODY layer (neutral base OR a gesture pose — always
 * mutually exclusive, so EXACTLY TWO ARMS are visible in every frame), a MOUTH
 * overlay (visemes), plus BROW + BLINK overlays. Identity is pixel-locked to the
 * master; only mouth/eyes/brow/arms ever change.
 *
 * Mouth motion is driven by the REAL branded-audio amplitude envelope (`level`,
 * 0..1) sampled from the cached line audio — never any synthetic browser voice.
 * When there is no audio (level stays 0) the mouth stays closed: no fake talking.
 */
export function CharacterRig({ id, speaking, level = 0, reduced, playing, width }) {
  const cfg = PUPPET[id];
  const aspect = cfg?.aspect ?? 0.86;
  const height = Math.round(width / aspect);

  const [viseme, setViseme] = useState("neutral");
  const [gesture, setGesture] = useState(null);
  const [blinkOn, setBlinkOn] = useState(false);

  const poseKeys = useMemo(() => Object.keys(cfg?.poses || {}), [cfg]);
  const openSeq = useMemo(() => OPEN_SEQ.filter((v) => cfg?.mouth?.[v]), [cfg]);

  const levelRef = useRef(0);
  useEffect(() => { levelRef.current = level; }, [level]);

  // --- Envelope-driven mouth sequencer (hysteresis + fast viseme cadence) ---
  const emaRef = useRef(0);
  const openIdxRef = useRef(0);
  const wasOpenRef = useRef(false);
  const lastStepRef = useRef(0);
  const gestUntilRef = useRef(0);
  useEffect(() => {
    if (!speaking || reduced) { setViseme("neutral"); setGesture(null); return; }
    const seq = openSeq.length ? openSeq : ["a"];
    let gestHold = null;
    const tick = setInterval(() => {
      const v = Math.max(0, Math.min(1, levelRef.current));
      emaRef.current = emaRef.current * 0.5 + v * 0.5;
      const e = emaRef.current;
      const now = Date.now();
      const open = wasOpenRef.current ? e > 0.12 : e > 0.17; // hysteresis around speech floor
      if (!open) { wasOpenRef.current = false; setViseme("neutral"); return; }
      wasOpenRef.current = true;
      if (now - lastStepRef.current < 90) return; // hold each shape a beat
      lastStepRef.current = now;
      openIdxRef.current += 1;
      const loud = e > 0.36;
      setViseme(loud && cfg?.mouth?.a && openIdxRef.current % 2 === 1 ? "a" : seq[openIdxRef.current % seq.length]);
      // Restrained arm gesture only on sustained loud beats, with cooldown.
      if (loud && poseKeys.length && now >= gestUntilRef.current) {
        gestUntilRef.current = now + 2600;
        setGesture(poseKeys[openIdxRef.current % poseKeys.length]);
        clearTimeout(gestHold);
        gestHold = setTimeout(() => setGesture(null), 900);
      }
    }, 55);
    return () => { clearInterval(tick); clearTimeout(gestHold); setViseme("neutral"); setGesture(null); wasOpenRef.current = false; };
  }, [speaking, reduced, openSeq, poseKeys, cfg]);

  // --- Blink (everyone with eyes, while playing). ---
  useEffect(() => {
    if (reduced || !cfg?.blink || !playing) { setBlinkOn(false); return; }
    let alive = true; let to;
    const cycle = () => {
      if (!alive) return;
      to = setTimeout(() => { setBlinkOn(true); setTimeout(() => { setBlinkOn(false); cycle(); }, 110); }, 2200 + Math.random() * 3200);
    };
    cycle();
    return () => { alive = false; clearTimeout(to); setBlinkOn(false); };
  }, [reduced, cfg, playing]);

  if (!cfg) return null;
  const bodySrc = gesture && cfg.poses?.[gesture] ? cfg.poses[gesture] : cfg.base; // mutually exclusive → 2 arms
  const mouthSrc = viseme !== "neutral" ? cfg.mouth?.[viseme] : null;
  const browOn = viseme === "a" && speaking;
  const anim = !reduced && (speaking || playing) ? `cs-breathe ${speaking ? "3.1s" : "5.2s"} ease-in-out infinite` : "none";

  return (
    <div className="cs-rig-layer" data-testid={`rig-${id}`} style={{ position: "relative", width, height, animation: anim }}>
      <img src={bodySrc} alt="" draggable={false} />
      {mouthSrc ? <img src={mouthSrc} alt="" draggable={false} /> : null}
      {cfg.brow ? <img src={cfg.brow} alt="" draggable={false} style={{ opacity: browOn ? 0.9 : 0, transition: "opacity 160ms ease" }} /> : null}
      {cfg.blink ? <img src={cfg.blink} alt="" draggable={false} style={{ opacity: blinkOn ? 1 : 0 }} /> : null}
    </div>
  );
}
